import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { Customer, CustomerForm, CustomerSearchForm } from 'entities/customer'

type CustomersResponse = PagingResponse & {
    results: Customer[]
}

export function useFetchCustomersApi(searchForm?: Form<CustomerSearchForm>): IndexApiSet<CustomersResponse> & { execute: () => void } {
    const apiPath = 'customers/'
    const api = useIndexApi<CustomersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchCustomerApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `customers/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostCustomerApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerForm>) => void } {
    const apiSet = usePostApi<BaseResponse, CustomerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CustomerForm>) => {
        const apiPath = `customers/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutCustomerApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerForm>) => void } {
    const apiSet = usePutApi<BaseResponse, CustomerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CustomerForm>) => {
        const apiPath = `customers/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutCustomersApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, CustomerForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CustomerForm[]>) => {
        const apiPath = `customers/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteCustomerApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `customers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useCreateRequestFormsOfCustomerOrderApi(): ApiSet<BaseResponse> & { execute: () => void } {
    const apiSet = usePostApi<BaseResponse, CustomerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = () => {
        const apiPath = `customers/create_request_forms/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}
